import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Busca tasks ligadas a um cliente — por termo (nome) OU por contactId direto.
 *
 * Resolve contatos por tokens (multi-palavra, AND entre tokens, OR entre
 * first_name/last_name), depois traz tasks por 3 caminhos:
 *  A) tasks.contact_id IN contactIds
 *  B) tasks.deal_id pertencente a deals com contact_id IN contactIds
 *     (usa join `deals!inner` para evitar URL gigante — regra do projeto)
 *  C) tasks de Escalação CS via support_sessions.contact_id
 *
 * Saída: array desduplicado com shape compatível com useFilteredTasks.
 */
export function useTasksByClient(params: { term?: string | null; contactId?: string | null }) {
  const term = (params.term ?? '').trim();
  const contactId = params.contactId ?? null;
  const enabled = !!contactId || term.length >= 2;

  const { data } = useQuery({
    queryKey: ['tasks-by-client', contactId, term],
    queryFn: async (): Promise<any[]> => {
      if (!enabled) return [];

      // 1) Resolver contactIds
      let contactIds: string[] = [];
      if (contactId) {
        contactIds = [contactId];
      } else {
        const tokens = term.split(/\s+/).filter(Boolean);
        let qb: any = supabase.from('contacts').select('id').limit(100);
        for (const tok of tokens) {
          const safe = tok.replace(/[%,()]/g, ' ');
          qb = qb.or(`first_name.ilike.%${safe}%,last_name.ilike.%${safe}%`);
        }
        const { data: contacts, error } = await qb;
        if (error) {
          console.error('[useTasksByClient] contacts error:', error);
          return [];
        }
        contactIds = (contacts ?? []).map((c: any) => c.id);
      }
      if (contactIds.length === 0) return [];

      const selectStr =
        '*, assigned_user:app_users!tasks_assigned_to_fkey(id, first_name, last_name, display_name)';

      // 2) Buscar tasks pelos 3 caminhos em paralelo
      const [directRes, dealRes, sessionsRes] = await Promise.all([
        supabase.from('tasks').select(selectStr).in('contact_id', contactIds).limit(500),
        supabase
          .from('tasks')
          .select(`${selectStr}, deals!inner(contact_id)`)
          .in('deals.contact_id', contactIds)
          .limit(500),
        supabase.from('support_sessions').select('id').in('contact_id', contactIds),
      ]);

      if (directRes.error) console.error('[useTasksByClient] direct error:', directRes.error);
      if (dealRes.error) console.error('[useTasksByClient] deal error:', dealRes.error);
      if (sessionsRes.error) console.error('[useTasksByClient] sessions error:', sessionsRes.error);

      const sessionIds = (sessionsRes.data ?? []).map((s: any) => s.id);
      let escTasks: any[] = [];
      if (sessionIds.length > 0) {
        const { data: esc, error: eErr } = await supabase
          .from('tasks')
          .select(selectStr)
          .eq('metadata->>source', 'cs_escalation')
          .in('metadata->>session_id', sessionIds)
          .limit(500);
        if (eErr) console.error('[useTasksByClient] escalation error:', eErr);
        escTasks = esc ?? [];
      }

      const all = [
        ...((directRes.data as any[]) ?? []),
        ...((dealRes.data as any[]) ?? []),
        ...escTasks,
      ];

      const map = new Map<string, any>();
      for (const t of all) {
        if (map.has(t.id)) continue;
        map.set(t.id, {
          ...t,
          assigned_user_name:
            t.assigned_user?.display_name ||
            [t.assigned_user?.first_name, t.assigned_user?.last_name].filter(Boolean).join(' ') ||
            null,
          assigned_user_first_name: t.assigned_user?.first_name ?? null,
          assigned_user_last_name: t.assigned_user?.last_name ?? null,
        });
      }
      return Array.from(map.values());
    },
    enabled,
    staleTime: 60 * 1000,
  });

  return data ?? [];
}
